import React, { useContext, useState } from 'react';
import { AuthContext } from '../context/AuthContext';
import { ChatContext } from '../context/ChatContext';
import { Timestamp, arrayUnion, doc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { db, storage } from '../firebase';
import { v4 as uuidv4 } from 'uuid';
import { getDownloadURL, ref, uploadBytesResumable } from 'firebase/storage';

const Input = () => {
  const { currentUser } = useContext(AuthContext);
  const { data } = useContext(ChatContext);
  const [text, setText] = useState("");
  const [img, setImg] = useState(null);
  const [audioFile, setAudioFile] = useState(null);
  const [docFile, setDocFile] = useState(null);
  const [videoFile, setVideoFile] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);

  const handleSend = async () => {
    if (!currentUser || !data.chatId) {
      console.error("Current user or chat ID not available.");
      return;
    }

    try {
      let downloadURL = null;
      const messageData = {
        id: uuidv4(),
        senderId: currentUser.uid,
        date: Timestamp.now(),
        text: text || "",
      };

      if (img) {
        const storageRef = ref(storage, `chats/${data.chatId}/${uuidv4()}`);
        const uploadTask = uploadBytesResumable(storageRef, img);

        downloadURL = await new Promise((resolve, reject) => {
          uploadTask.on(
            "state_changed",
            null,
            (error) => {
              console.error("Error uploading image:", error);
              reject(error);
            },
            async () => {
              const url = await getDownloadURL(uploadTask.snapshot.ref);
              resolve(url);
            }
          );
        });

        messageData.img = downloadURL;
        setImg(null);
        document.getElementById('imageInput').value = null;
      } else if (audioFile) {
        const audioStorageRef = ref(storage, `chats/${data.chatId}/${uuidv4()}`);
        const audioUploadTask = uploadBytesResumable(audioStorageRef, audioFile);

        downloadURL = await new Promise((resolve, reject) => {
          audioUploadTask.on(
            "state_changed",
            null,
            (error) => {
              console.error("Error uploading audio:", error);
              reject(error);
            },
            async () => {
              const url = await getDownloadURL(audioUploadTask.snapshot.ref);
              resolve(url);
            }
          );
        });

        messageData.audio = downloadURL;
        setAudioFile(null);
        document.getElementById('audioInput').value = null;
      } else if (docFile) {
        const docStorageRef = ref(storage, `chats/${data.chatId}/${uuidv4()}`);
        const docUploadTask = uploadBytesResumable(docStorageRef, docFile);

        downloadURL = await new Promise((resolve, reject) => {
          docUploadTask.on(
            "state_changed",
            null,
            (error) => {
              console.error("Error uploading document:", error);
              reject(error);
            },
            async () => {
              const url = await getDownloadURL(docUploadTask.snapshot.ref);
              resolve(url);
            }
          );
        });

        messageData.file = downloadURL;
        messageData.fileType = docFile.name.split('.').pop();
        setDocFile(null);
        document.getElementById('docInput').value = null;
      } else if (videoFile) {
        const videoStorageRef = ref(storage, `chats/${data.chatId}/${uuidv4()}`);
        const videoUploadTask = uploadBytesResumable(videoStorageRef, videoFile);

        downloadURL = await new Promise((resolve, reject) => {
          videoUploadTask.on(
            "state_changed",
            null,
            (error) => {
              console.error("Error uploading video:", error);
              reject(error);
            },
            async () => {
              const url = await getDownloadURL(videoUploadTask.snapshot.ref);
              resolve(url);
            }
          );
        });

        messageData.video = downloadURL;
        setVideoFile(null);
        document.getElementById('videoInput').value = null;
      }

      await updateDoc(doc(db, "chats", data.chatId), {
        messages: arrayUnion(messageData),
      });

      await updateDoc(doc(db, "userChats", currentUser.uid), {
        [`${data.chatId}.lastMessage`]: {
          text: text || "Media",
        },
        [`${data.chatId}.date`]: serverTimestamp(),
      });

      await updateDoc(doc(db, "userChats", data.user.uid), {
        [`${data.chatId}.lastMessage`]: {
          text: text || "Media",
        },
        [`${data.chatId}.date`]: serverTimestamp(),
      });

      setText("");
      setImg(null);
      setAudioFile(null);
      setDocFile(null);
      setVideoFile(null);
    } catch (error) {
      console.error("Error sending message:", error);
    }

  };

  const handleOptionSelect = (option) => {
    if (option === 'image') {
      document.getElementById('imageInput').click();
    } else if (option === 'audio') {
      document.getElementById('audioInput').click();
    } else if (option === 'doc') {
      document.getElementById('docInput').click();
    } else if (option === 'video') {
      document.getElementById('videoInput').click();
    }
    setShowDropdown(false);
  };

  return (
    <div className='chat-input'>
      <div className="chat-input__wrapper">
        <div className="chat-input__attach-wrapper">
          <button className="chat-input__attach" onClick={() => setShowDropdown(!showDropdown)}>
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12C21 16.9706 16.9706 21 12 21Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M12 8V16M8 12H16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          
          {showDropdown && (
            <div className="chat-input__dropdown">
              <div className="chat-input__dropdown-item" onClick={() => handleOptionSelect('image')}>
                <span>🖼️ Image</span>
              </div>
              <div className="chat-input__dropdown-item" onClick={() => handleOptionSelect('audio')}>
                <span>🎵 Audio</span>
              </div>
              <div className="chat-input__dropdown-item" onClick={() => handleOptionSelect('doc')}>
                <span>📄 Document</span>
              </div>
              <div className="chat-input__dropdown-item" onClick={() => handleOptionSelect('video')}>
                <span>🎥 Video</span>
              </div>
            </div>
          )}
        </div>

        <input
          type="text"
          className="chat-input__text"
          placeholder='Type something...'
          onChange={(e) => setText(e.target.value)}
          value={text}
          onKeyDown={(e) => { if (e.key === 'Enter') handleSend(); }}
        />

        <button className="chat-input__emoji">
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.5" />
            <path d="M8 9.5C8 10.3284 8.67157 11 9.5 11C10.3284 11 11 10.3284 11 9.5C11 8.67157 10.3284 8 9.5 8C8.67157 8 8 8.67157 8 9.5Z" fill="currentColor" />
            <path d="M16 9.5C16 10.3284 15.3284 11 14.5 11C13.6716 11 13 10.3284 13 9.5C13 8.67157 13.6716 8 14.5 8C15.3284 8 16 8.67157 16 9.5Z" fill="currentColor" />
            <path d="M8 15C9.5 16.5 11.5 17 14 15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </button>
        
        <input type="file" id='imageInput' style={{ display: 'none' }} onChange={(e) => setImg(e.target.files[0])} />
        <input type="file" id='audioInput' accept='audio/*' style={{ display: 'none' }} onChange={(e) => setAudioFile(e.target.files[0])} />
        <input type="file" id='docInput' accept='.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx' style={{ display: 'none' }} onChange={(e) => setDocFile(e.target.files[0])} />
        <input type="file" id='videoInput' accept='video/*' style={{ display: 'none' }} onChange={(e) => setVideoFile(e.target.files[0])} />

        <button className="chat-input__send" onClick={handleSend}>
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M22 2L11 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M22 2L15 22L11 13L2 9L22 2Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default Input;
